import React from "react";
import { formatNumber } from "@/lib/utils";
import { formatVnDateTime, planQcSampling } from "@apv-erp/status";
import ISOPrintHeader from "@/components/shared/print/ISOPrintHeader";
import { PrintDocumentTitle } from "@/components/shared/print";
import { QC_RESULT_LABELS, checklistItemsOf } from "@/components/quality/qc-form/checklist";
import { displayCriterionName, formatCriterionStandard, isQuantitativeCriterion } from "@/components/quality/qc-form/CriteriaTable";
import { isIpqcNewFlow, legacyParamRows, repeatColumnsOf } from "@/components/quality/qc-form/ipqc";
import {
  ROUND_RESULT_LABEL,
  frequencyLabel,
  normalizePatrolLogs,
  roundCellText,
  roundColumnLabel,
} from "@/components/quality/IpqcThreeStageSection";
import {
  QcPrintConclusion,
  QcPrintCriteriaTable,
  QcPrintDefects,
  QcPrintInfoGrid,
  QcPrintLegacy,
  QcPrintSamplingRow,
  QcPrintSignatures,
} from "./qc-print-blocks";

/**
 * BÁO CÁO KIỂM TRA CÔNG ĐOẠN (BM-QC-02), A4 dọc:
 * thông tin chung · lấy mẫu · 4M (1 bảng, dòng nhóm) · ký mẫu (1 hàng) · chỉ tiêu sản phẩm ·
 * kiểm tra lặp lại (mỗi lần 1 dòng, cột theo chỉ tiêu lặp lại; > 12 lần sang trang 2) · chốt lô 1 hàng · lỗi · kết luận · ký.
 * Phiếu cũ (không có 4M): bảng chỉ tiêu + thông số máy (dữ liệu cũ).
 */

interface QCProcessPrintTemplateProps {
  data?: any;
  config?: any;
}

const STEP_LABELS: Record<string, string> = {
  CUTTING: "Cắt giấy",
  PRINTING: "In offset",
  LAMINATING: "Cán màng",
  COATING: "Phủ UV",
  DIE_CUTTING: "Bế, ép kim",
  GLUING: "Dán hộp",
};
const SETUP_LABEL: Record<string, string> = { PASSED: "Đạt", ADJUST_REQUIRED: "Chỉnh máy", FAILED: "Không đạt", CONDITIONAL: "Nhân nhượng" };
const SHIFT_LABEL: Record<string, string> = { CA_1: "Ca 1", CA_2: "Ca 2", CA_3: "Ca 3", HC: "Hành chính" };
const ROUNDS_PAGE_1 = 12;

function FourMTable({ items }: { items: any[] }) {
  // 1 bảng 4 nhóm: dòng nhóm + các chỉ tiêu của nhóm
  const groups: Array<{ key: string; rows: any[] }> = [];
  for (const it of items) {
    const key = String(it.category || "Khác");
    let g = groups.find((x) => x.key === key);
    if (!g) groups.push((g = { key, rows: [] }));
    g.rows.push(it);
  }
  return (
    <div className="mb-2">
      <div className="mb-0.5 text-[9.5pt] font-bold uppercase">1. Duyệt setup (4M)</div>
      <table className="qc-print-table w-full table-fixed border-collapse border border-black text-[9pt]" data-testid="qc-print-4m">
        <thead>
          <tr className="bg-slate-100 text-center font-bold">
            <th className="border border-black px-1 py-0.5 text-left" style={{ width: "30%" }}>Chỉ tiêu</th>
            <th className="border border-black px-1 py-0.5 text-left" style={{ width: "32%" }}>Tiêu chuẩn</th>
            <th className="border border-black px-1 py-0.5" style={{ width: "11%" }}>Kết quả</th>
            <th className="border border-black px-1 py-0.5" style={{ width: "10%" }}>Đánh giá</th>
            <th className="border border-black px-1 py-0.5 text-left" style={{ width: "17%" }}>Ghi chú</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <React.Fragment key={g.key}>
              <tr className="bg-slate-50">
                <td colSpan={5} className="border border-black px-1 py-0 text-[8.5pt] font-bold">
                  {g.key}
                </td>
              </tr>
              {g.rows.map((it: any, idx: number) => {
                const r = String(it.result || "PENDING").toUpperCase();
                const quant = isQuantitativeCriterion(it);
                return (
                  <tr key={it.id || idx} className="break-inside-avoid leading-[1.15]">
                    <td className="border border-black px-1 py-0.5 font-semibold">{displayCriterionName(it, idx)}</td>
                    <td className={`border border-black px-1 py-0.5 ${quant ? "font-mono" : ""}`}>{formatCriterionStandard(it)}</td>
                    <td className="border border-black px-1 py-0.5 text-center font-mono">
                      {quant && it.measuredValue !== undefined && it.measuredValue !== null && it.measuredValue !== ""
                        ? `${it.measuredValue}${it.unit ? ` ${it.unit}` : ""}`
                        : ""}
                    </td>
                    <td className={`border border-black px-1 py-0.5 text-center font-bold ${r === "FAIL" ? "text-rose-800" : ""}`}>
                      {r === "PENDING" ? "—" : QC_RESULT_LABELS[r] || r}
                    </td>
                    <td className="qc-print-small border border-black px-1 py-0.5 text-[8pt]">{it.note || ""}</td>
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RoundsTable({ rounds, columns, legacy, title, testId, startIdx = 0 }: { rounds: any[]; columns: any[]; legacy: boolean; title: string; testId: string; startIdx?: number }) {
  return (
    <div className="mb-2">
      <div className="mb-0.5 text-[9.5pt] font-bold uppercase">{title}</div>
      <table className="qc-print-table w-full border-collapse border border-black text-[8.5pt]" data-testid={testId}>
        <thead>
          <tr className="bg-slate-100 text-center font-bold">
            <th className="border border-black px-1 py-0.5">Lần</th>
            <th className="border border-black px-1 py-0.5">Giờ</th>
            <th className="border border-black px-1 py-0.5">Mốc SL</th>
            <th className="border border-black px-1 py-0.5">Mẫu</th>
            <th className="border border-black px-1 py-0.5">Lỗi</th>
            {columns.map((c: any) => (
              <th key={c.id} className="border border-black px-1 py-0.5">
                {roundColumnLabel(c)}
              </th>
            ))}
            <th className="border border-black px-1 py-0.5">Kết quả</th>
            <th className="border border-black px-1 py-0.5 text-left">{legacy ? "Số đo / ghi chú" : "Ghi chú"}</th>
          </tr>
        </thead>
        <tbody>
          {rounds.map((r: any, i: number) => (
            <tr key={startIdx + i} className="break-inside-avoid text-center">
              <td className="border border-black px-1 py-0.5 font-mono">{r.roundNo || startIdx + i + 1}</td>
              <td className="border border-black px-1 py-0.5 font-mono">{r.checkTime || "—"}</td>
              <td className="border border-black px-1 py-0.5 font-mono">{formatNumber(r.runningQty || 0)}</td>
              <td className="border border-black px-1 py-0.5 font-mono">{r.sampleQty}</td>
              <td className="border border-black px-1 py-0.5 font-mono">{r.ngQty}{r.defectCode ? ` (${r.defectCode})` : ""}</td>
              {columns.map((c: any) => (
                <td key={c.id} className="border border-black px-1 py-0.5 font-mono">
                  {roundCellText(c, r.values?.[c.id])}
                </td>
              ))}
              <td className="border border-black px-1 py-0.5 font-bold">{ROUND_RESULT_LABEL[r.result] || "Đạt"}</td>
              <td className="qc-print-small border border-black px-1 py-0.5 text-left text-[8pt]">
                {[r.measuredReading, r.note].filter(Boolean).join(" · ")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function QCProcessPrintTemplate({ data = {}, config }: QCProcessPrintTemplateProps) {
  const docId =
    data?.processId || data?.code || (data?.id?.length > 20 ? `IPQC-${data.id.slice(0, 8).toUpperCase()}` : data?.id) || "";
  const newFlow = isIpqcNewFlow(data);
  const fourM = newFlow ? checklistItemsOf(data.fourMChecklist) : [];
  const checklist = checklistItemsOf(data.checklist);
  const rounds = normalizePatrolLogs(data.patrolLogs);
  const columns = repeatColumnsOf(fourM, checklist);
  const defects = Array.isArray(data.defectDetails) ? data.defectDetails : Array.isArray(data.defectBreakdown) ? data.defectBreakdown : [];
  const lotSize = Number(data.lotSize || data.productionOrder?.qtyPlan || 0);
  const sampled = Number(data.qtySample ?? data.sampleSize ?? 0);
  const ng = Number(data.qtyDefect ?? data.defectQty ?? 0);
  const stage = data.processStage || "";
  // Cỡ mẫu theo bảng (tổng cỡ lô); số mẫu đã kiểm ở hàng Chốt lô
  const plannedSample =
    lotSize > 0 && data.reMajor
      ? planQcSampling({ lotSize, mode: data.inspectionMode, level: data.aqlLevel, aqlMajor: Number(data.aqlMajor) || 1.5, aqlMinor: data.aqlMinor ?? null }).sampleSize
      : sampled;
  const product = data.product || data.productionOrder?.product;
  const customer = data.customer || data.productionOrder?.customer;

  const info: Array<[string, React.ReactNode]> = [
    ["Lệnh SX", data.productionOrder?.orderId || "—"],
    ["Công đoạn", data.processStep || STEP_LABELS[stage] || stage],
    ["Máy", data.machine?.name || data.machine?.machineId || "—"],
    ["Ca / Thợ", [SHIFT_LABEL[data.shift] || data.shift, data.operatorName].filter(Boolean).join(" · ") || "—"],
    ["Sản phẩm", product ? [product.productCode, product.productName].filter(Boolean).join(" · ") : data.productionOrder?.productName],
    ["Khách hàng", customer?.shortName || customer?.name],
    ["Mẫu chuẩn", data.referenceSampleNo],
    ["Tần suất", frequencyLabel(data.sampleFrequency)],
  ];

  const page1 = rounds.slice(0, ROUNDS_PAGE_1);
  const page2 = rounds.slice(ROUNDS_PAGE_1);

  return (
    <div
      className="p-[8mm] text-black leading-tight text-[9pt] bg-white w-full max-w-[210mm] mx-auto"
      style={{ fontFamily: "'Times New Roman', Times, serif" }}
      id="print-area"
    >
      <ISOPrintHeader config={config} />
      <PrintDocumentTitle
        title="BÁO CÁO KIỂM TRA CÔNG ĐOẠN"
        docNo={docId}
        docNoLabel="Số phiếu"
        extraMeta="BM-QC-02"
        date={data.checkTime || data.createdAt || new Date()}
        dateLabel="Ngày kiểm"
      />

      <QcPrintInfoGrid items={info} />
      <QcPrintSamplingRow
        lotSize={lotSize}
        mode={data.inspectionMode}
        level={data.aqlLevel}
        aqlMajor={data.aqlMajor}
        aqlMinor={data.aqlMinor}
        sampleSize={plannedSample}
        acMajor={data.acMajor}
        reMajor={data.reMajor}
        acMinor={data.acMinor}
        reMinor={data.reMinor}
      />

      {newFlow ? (
        <>
          <FourMTable items={fourM} />
          <table className="qc-print-table mb-2 w-full border-collapse border border-black text-[9pt]" data-testid="qc-print-signoff">
            <tbody>
              <tr>
                <td className="border border-black bg-slate-100 px-1 py-0.5 font-bold uppercase" style={{ width: "13%" }}>
                  2. Ký mẫu
                </td>
                <td className="border border-black px-1 py-0.5">
                  Setup: <b>{SETUP_LABEL[data.setupResult] || "—"}</b>
                </td>
                <td className="border border-black px-1 py-0.5">
                  Mẫu ký: <b>{data.setupSampleQty ?? 0}</b> (lỗi {data.setupDefectQty ?? 0}){data.sampleRef ? ` · ${data.sampleRef}` : ""}
                </td>
                <td className="border border-black px-1 py-0.5">
                  Ký: <b>{data.sampleApprovedAt ? formatVnDateTime(data.sampleApprovedAt) : "—"}</b>
                  {data.sampleApprovedByName ? ` · ${data.sampleApprovedByName}` : ""}
                  {data.sampleShiftLead ? ` · Trưởng ca ${data.sampleShiftLead}` : ""}
                </td>
                <td className="border border-black px-1 py-0.5">
                  Cho SX: <b>{data.releasedAt ? formatVnDateTime(data.releasedAt) : "—"}</b>
                </td>
              </tr>
            </tbody>
          </table>
          {checklist.length > 0 && <QcPrintCriteriaTable items={checklist} title="Chỉ tiêu sản phẩm (ký mẫu)" />}
        </>
      ) : (
        <>
          <div className="mb-2 border border-black px-1.5 py-0.5" data-testid="qc-print-signoff">
            <b>Duyệt setup:</b> {SETUP_LABEL[data.setupResult] || "—"} · Mẫu {data.setupSampleQty ?? 5} (lỗi {data.setupDefectQty ?? 0})
            {data.setupNotes ? ` · ${data.setupNotes}` : ""}
          </div>
          <QcPrintCriteriaTable items={checklist} />
          <QcPrintLegacy rows={legacyParamRows(data.technicalParams)} />
        </>
      )}

      {rounds.length > 0 && (
        <RoundsTable
          rounds={page1}
          columns={columns}
          legacy={!newFlow}
          title={`${newFlow ? "3. " : ""}Kiểm tra lặp lại (${rounds.length} lần)`}
          testId="qc-print-rounds"
        />
      )}

      <table className="qc-print-table mb-2 w-full border-collapse border border-black text-[9pt]" data-testid="qc-print-lot">
        <tbody>
          <tr className="text-center">
            <td className="border border-black bg-slate-100 px-1 py-0.5 font-bold uppercase" style={{ width: "13%" }}>
              {newFlow ? "4. Chốt lô" : "Chốt lô"}
            </td>
            <td className="border border-black px-1 py-0.5">
              Cỡ lô <b>{formatNumber(lotSize)}</b>
            </td>
            <td className="border border-black px-1 py-0.5">
              Tổng mẫu <b>{formatNumber(sampled)}</b>
            </td>
            <td className="border border-black px-1 py-0.5">
              Mẫu lỗi <b>{formatNumber(ng)}</b>
            </td>
            <td className="border border-black px-1 py-0.5">
              Số lần kiểm <b>{rounds.length}</b>
            </td>
          </tr>
        </tbody>
      </table>

      <QcPrintDefects items={defects} />
      <QcPrintConclusion
        status={data.status}
        counts={{ critical: data.criticalDefects || 0, major: data.majorDefects || 0, minor: data.minorDefects || 0 }}
        reMajor={data.reMajor}
        reMinor={data.reMinor}
        overrideReason={data.overrideReason}
        notes={data.notes}
      />
      <QcPrintSignatures
        columns={[
          { title: "Người kiểm", hint: "(Ký, ghi rõ họ tên)", name: data.inspector?.fullName || "" },
          { title: "Trưởng ca", hint: "(Xác nhận mẫu, thông số)", name: data.sampleShiftLead || "" },
          { title: "Trưởng QC", hint: "(Duyệt)" },
        ]}
      />

      {page2.length > 0 && (
        <div style={{ breakBefore: "page", pageBreakBefore: "always" }} className="pt-[4mm]" data-testid="qc-print-page2">
          <div className="mb-1 text-[9pt] font-bold">Trang 2 · Báo cáo kiểm tra công đoạn (tiếp)</div>
          <RoundsTable
            rounds={page2}
            columns={columns}
            legacy={!newFlow}
            title="Kiểm tra lặp lại (tiếp)"
            testId="qc-print-rounds-page2"
            startIdx={ROUNDS_PAGE_1}
          />
        </div>
      )}
    </div>
  );
}
